import { useContext, useMemo } from 'react';
import { useAppSelector } from '@/features';
import { UserSessionContext } from '@/providers/UserSessionProvider';
import {
  ConnectorStatusEnum,
  Measurand2D5Enum,
  type Connector,
  type SampledValue,
} from '@/types/swagger';
import {
  convertDateToTimezone,
  formatDateToFriendlyString,
  getCurrentDateInTimezone,
} from '@/util/date-formatters';
import getSocByConnector from '@/util/getSocByConnector';
import roundToTwo from '@/util/roundToTwo';
import { addSeconds, compareAsc } from 'date-fns';

export const useTransactionValues = (connector: Connector | null) => {
  const { timezone } = useContext(UserSessionContext);
  const statusNotification = connector?.latest_status_notification;
  const transaction = useMemo(() => connector?.latest_transaction || null, [connector]);
  const sampledValues: SampledValue[] = useAppSelector(
    (state) => state.transactions.sampledValuesByTransactionId[transaction?.id || 0]
  );
  const startTime = transaction?.timestamp_start
    ? formatDateToFriendlyString(transaction.timestamp_start, timezone)
    : null;
  const endTime = transaction?.timestamp_stop
    ? formatDateToFriendlyString(transaction.timestamp_stop, timezone)
    : null;
  const remainingTime = useMemo(
    () =>
      transaction?.remaining_time ? Math.ceil(parseFloat(transaction.remaining_time) * 3600) : null,
    [transaction]
  );
  const estimatedEndTime = useMemo(
    () =>
      remainingTime
        ? formatDateToFriendlyString(
            addSeconds(getCurrentDateInTimezone(timezone), remainingTime).toISOString(),
            timezone
          )
        : null,
    [remainingTime, timezone]
  );

  const vehicle = transaction ? transaction.vehicle : null;

  const soc = sampledValues
    ? sampledValues.find((dataItem) => dataItem.measurand === 'SoC')
    : undefined;

  const batterySoc = connector
    ? getSocByConnector(connector, 'connector') || parseFloat(soc?.value || '0')
    : 0;

  const power = sampledValues
    ?.sort((a, b) => {
      const aDate = convertDateToTimezone(a.timestamp, timezone);
      const bDate = convertDateToTimezone(b.timestamp, timezone);
      return compareAsc(bDate, aDate);
    })
    .find((dataItem) => dataItem.measurand === Measurand2D5Enum.PowerActiveImport);

  const energyImport = sampledValues
    ?.sort((a, b) => {
      const aDate = convertDateToTimezone(a.timestamp, timezone);
      const bDate = convertDateToTimezone(b.timestamp, timezone);
      return compareAsc(bDate, aDate);
    })
    .find((dataItem) => dataItem.measurand === Measurand2D5Enum.EnergyActiveImportRegister);
  const { consumption, capacity } = {
    consumption: vehicle?.consumption || null,
    capacity: vehicle?.battery_nominal_capacity || null,
  };

  const absoluteSoc =
    capacity && (batterySoc || batterySoc === 0) ? (batterySoc * capacity) / 100 : 0;

  const autonomy =
    (absoluteSoc || absoluteSoc === 0) && consumption ? absoluteSoc / consumption : 0;

  const status = statusNotification
    ? (statusNotification.status as unknown as ConnectorStatusEnum)
    : ConnectorStatusEnum.Available;

  const colorByStatus: Partial<Record<ConnectorStatusEnum, string>> = useMemo(
    () => ({
      [ConnectorStatusEnum.Preparing]: 'gray',
      [ConnectorStatusEnum.Charging]: '#405fb9',
      [ConnectorStatusEnum.Finishing]: '#437f02',
    }),
    []
  );

  const donutData = useMemo(() => {
    return [
      {
        name: 'Carga',
        value: batterySoc,
        color: colorByStatus[status] as string,
      },
      {
        name: 'Restante',
        value: 100 - batterySoc,
        color: '#FFFFFF',
      },
    ];
  }, [batterySoc, status, colorByStatus]);

  return {
    connectorId: connector?.id || 0,
    status,
    vehicle,
    batterySoc: batterySoc ? `${Math.round(batterySoc)}%` : null,
    autonomy: autonomy || autonomy === 0 ? `${Math.round(autonomy)} km` : null,
    absoluteSoc,
    capacity,
    chargedEnergy:
      energyImport && !!transaction?.meter_start
        ? `${roundToTwo((parseFloat(energyImport.value) - transaction.meter_start) / 1000)} kWh`
        : transaction?.meter_start && transaction?.meter_stop
          ? `${roundToTwo((transaction.meter_stop - transaction.meter_start) / 1000)} kWh`
          : '- - -',
    power: power ? `${roundToTwo(parseFloat(power.value) / 1000)} kW` : '- - -',
    startTime,
    endTime,
    transaction,
    estimatedEndTime,
    remainingTime: remainingTime
      ? remainingTime <= 60
        ? `${Math.round(remainingTime)} seg.`
        : `${Math.round(remainingTime / 60)} min.`
      : null,
    donutData,
    batteryChargePercentage: `${Math.round(absoluteSoc / 1000)}/${capacity ? Math.round(capacity / 1000) : '- - -'}`,
  };
};
